/**
 * PostHog bootstrap. These options configure the provider that wraps the whole
 * CRA (see index.tsx).
 *
 * - `persistence: 'memory'` -> cookieless, no cookie banner needed
 * - `autocapture: false` -> the app renders signatory names, emails and IBANs in
 *   clickable rows; autocapture would ship that DOM text ($el_text) to PostHog.
 *   Only explicit product events are sent (GDPR audit NC-15).
 * - `before_send` -> every URL leaves without its #fragment (see below)
 * - Disabled when REACT_APP_POSTHOG_KEY is missing (safe for local dev)
 */

import type { BeforeSendFn, CaptureResult } from 'posthog-js'

export const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY || ''
export const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://eu.i.posthog.com'

/**
 * A magic-link sign-in lands on /home#access_token=...&refresh_token=..., and
 * supabase-js clears that fragment only after a network round trip. PostHog
 * reads location.href earlier, at init, and its masking covers the query
 * string only. So the session tokens, and the email inside the access token,
 * reached PostHog in $current_url and in the person's initial URL.
 * The app routes by path and needs no fragment in analytics: every http(s)
 * URL in an event, however deeply nested, is sent without it.
 * See docs/audits/2026-09-23-audit-securite.md (S-03).
 */
function stripUrlFragments(value: unknown): unknown {
  if (typeof value === 'string') {
    return /^https?:\/\//i.test(value) ? value.split('#')[0] : value
  }
  if (Array.isArray(value)) {
    return value.map(stripUrlFragments)
  }
  if (value !== null && typeof value === 'object' && !(value instanceof Date)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, nested]) => [key, stripUrlFragments(nested)])
    )
  }
  return value
}

export const stripUrlFragmentsBeforeSend: BeforeSendFn = (event) =>
  event ? (stripUrlFragments(event) as CaptureResult) : null

export const posthogOptions = {
  api_host: POSTHOG_HOST,
  persistence: 'memory' as const,       // cookieless: no banner needed
  capture_pageview: true,                // pageviews only, no DOM text
  capture_pageleave: true,               // track when users leave
  autocapture: false,                    // never auto-capture clicked text (names, IBANs)
  mask_personal_data_properties: true,   // masks known PII query params (not the #fragment)
  before_send: stripUrlFragmentsBeforeSend, // no URL leaves with its #fragment (magic-link tokens)
  capture_exceptions: true,              // auto-capture uncaught errors + unhandled promise rejections
  disable_session_recording: true,       // no session replay (privacy)
  cross_subdomain_cookie: false,
  secure_cookie: true,
}
